// Shows.jsx - Gold shows, for the people who might come to one. The list of them,
// and one show's own page with the form that says you are coming.
//
// The whole design decision on this page: telling us you are coming is three
// fields and a time, and it is never a requirement. Somebody can read the page,
// note the address and turn up, and nothing on the page suggests otherwise.
// Registering exists so we have the right scale out and are not all at lunch when
// they arrive, and the copy says exactly that rather than dressing it up as a
// booking.
//
// These pages are not prerendered, because a show lives in the database and the
// prerenderer runs at build time with no database. The Event structured data is
// therefore written on the client, which search engines do read, and it is the
// one page type on this site where that tradeoff exists.
//
// LEGEND
//   1. Shows      the index, upcoming then past
//   2. Show       one show, its gallery and its form
//   3. Coming     the registration form
//   4. Gallery    photographs from this show or the last one

import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { Seo, JsonLd, breadcrumbSchema, SITE } from "../lib/seo.jsx";
import { useAsync, useForm } from "../lib/hooks.js";
import { publicApi } from "../lib/api.js";
import * as analytics from "../lib/analytics.js";
import { Panel, Input, Textarea, Select, Checkbox, Button, Alert, Loading, Empty } from "../ui/kit.jsx";
import { ArrowRight, Clock, CheckCircle, Scale } from "../ui/icons.jsx";
import { Reveal, StaggerBoxed } from "../ui/motion.jsx";
import { PageHead, CtaBand, MobileCta } from "./parts.jsx";
import NotFound from "./NotFound.jsx";

// Dates arrive as plain YYYY-MM-DD because a show happens in its venue's local
// time and nobody at the door is thinking in UTC. Parsing them as UTC and
// formatting them as UTC is what stops a show on the 4th showing as the 3rd.
const dayName = (date) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", timeZone: "UTC",
  });

const shortDay = (date) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

const clockTime = (time) => {
  const [hour, minute] = String(time).split(":").map(Number);
  const suffix = hour >= 12 ? "pm" : "am";
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return minute === 0 ? `${twelve}${suffix}` : `${twelve}:${String(minute).padStart(2, "0")}${suffix}`;
};

const placeOf = (show) => [show.venue || show.venue?.name, show.city, show.state].filter(Boolean).join(", ");

////////// 1. THE LIST //////////

export default function Shows() {
  const { data, loading, error } = useAsync((signal) => publicApi.shows(signal), []);

  return (
    <>
      <Seo
        title="Gold shows and buying events"
        path="/shows"
        description="Where to find us in person. We bring the scales, the testers and the camera to buying events across the country, and you can see what your gold is worth while you wait."
      />
      <JsonLd data={breadcrumbSchema([{ label: "Home", to: "/" }, { label: "Shows", to: "/shows" }])} />

      <PageHead
        eyebrow="In person"
        title="Where to find us."
        lede="We run buying events across the country. Bring what you have, we weigh and test it in front of you, and you decide whether to sell. There is nothing to pay and nothing to commit to."
      />

      <div className="shell pb-14 md:pb-20">
        {loading && <Loading label="Loading shows" />}
        {error && <Alert tone="error" title="Could not load the shows">{error.message}</Alert>}

        {data && data.upcoming.length === 0 && data.past.length === 0 && (
          <Empty title="No shows on the calendar" icon={Scale}>
            Nothing is booked at the moment. The mail-in kit works from anywhere in the country, and it is free both ways.
          </Empty>
        )}

        {data?.upcoming.length > 0 && (
          <section>
            <p className="eyebrow mb-5">Coming up</p>
            <StaggerBoxed as="div" step={90} className="grid gap-px bg-rule border border-rule sm:grid-cols-2" itemClassName="bg-paper h-full">
              {data.upcoming.map((show) => <ShowCard key={show.slug} show={show} />)}
            </StaggerBoxed>
          </section>
        )}

        {data?.past.length > 0 && (
          <section className="mt-14">
            <p className="eyebrow mb-5">Where we have been</p>
            <StaggerBoxed as="div" step={70} className="grid gap-px bg-rule border border-rule sm:grid-cols-3" itemClassName="bg-paper h-full">
              {data.past.map((show) => <ShowCard key={show.slug} show={show} past />)}
            </StaggerBoxed>
          </section>
        )}
      </div>

      <CtaBand
        title="Not near one of these?"
        body="The mail-in kit does the same job by post. Free both ways, insured, and every piece weighed on camera."
      />
      <MobileCta />
    </>
  );
}

function ShowCard({ show, past = false }) {
  return (
    <Link to={`/shows/${show.slug}`} className="group block bg-paper hover:bg-surface transition-colors p-5 sm:p-6">
      {show.cover && (
        <img src={show.cover} alt="" loading="lazy" className="w-full aspect-[3/2] object-cover mb-4 border border-rule" />
      )}
      <p className="eyebrow">{show.days.map((day) => shortDay(day.date)).join(" to ")}</p>
      <h2 className="display display-sm text-ink mt-1.5 group-hover:text-bronze-dark transition-colors">{show.title}</h2>
      <p className="hint mt-1.5">{[show.venue, show.city, show.state].filter(Boolean).join(", ")}</p>
      {!past && show.summary && <p className="hint mt-2.5 leading-relaxed line-clamp-2">{show.summary}</p>}
      <span className="inline-flex items-center gap-1.5 mt-3 text-[13px] font-semibold text-ink">
        {past ? "See the photographs" : "Details and times"}
        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
      </span>
    </Link>
  );
}

////////// 2. ONE SHOW //////////

export function Show() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const { data: show, loading, error, reload } = useAsync((signal) => publicApi.show(slug, signal), [slug]);

  const schema = useMemo(() => {
    if (!show) return null;
    const first = show.days[0];
    return {
      "@context": "https://schema.org",
      "@type": "Event",
      name: show.title,
      description: show.summary || show.body?.slice(0, 300),
      startDate: first ? `${first.date}T${first.opens}` : undefined,
      endDate: show.days.length ? `${show.days.at(-1).date}T${show.days.at(-1).closes}` : undefined,
      eventStatus: show.cancelled ? "https://schema.org/EventCancelled" : "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      location: {
        "@type": "Place",
        name: show.venue.name || show.title,
        address: {
          "@type": "PostalAddress",
          streetAddress: [show.venue.line1, show.venue.line2].filter(Boolean).join(", "),
          addressLocality: show.venue.city,
          addressRegion: show.venue.state,
          postalCode: show.venue.postal,
          addressCountry: "US",
        },
      },
      image: show.cover ? [show.cover] : undefined,
      isAccessibleForFree: true,
      organizer: { "@type": "Organization", name: "International Gold & Silver", url: SITE },
      url: `${SITE}/shows/${slug}`,
    };
  }, [show, slug]);

  if (loading) return <Loading label="Loading the show" className="min-h-[50vh]" />;
  if (error) return <NotFound />;

  return (
    <>
      <Seo
        title={`${show.title}, ${show.venue.city} ${show.venue.state}`}
        path={`/shows/${slug}`}
        description={show.summary || `We are buying gold and silver at ${show.venue.name || show.title} in ${show.venue.city}. Free appraisal, no obligation.`}
      />
      <JsonLd data={schema} />
      <JsonLd data={breadcrumbSchema([
        { label: "Home", to: "/" },
        { label: "Shows", to: "/shows" },
        { label: show.title, to: `/shows/${slug}` },
      ])} />

      <PageHead
        eyebrow={show.days.map((day) => shortDay(day.date)).join(" to ")}
        title={show.title}
        lede={show.summary}
      />

      <div className="shell pb-14 md:pb-20">
        {show.cancelled && (
          <Alert tone="warning" className="mb-8" title="This show has been cancelled">
            If you registered, we have emailed you. Call us and we will sort out a kit instead.
          </Alert>
        )}

        <div className="grid gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14 items-start">
          <div className="min-w-0">
            {show.body && <div className="prose-igs whitespace-pre-line">{show.body}</div>}

            <Panel className="p-5 mt-8">
              <p className="eyebrow mb-3">Where</p>
              <p className="text-[15px] text-ink leading-relaxed">
                {show.venue.name && <span className="block font-semibold">{show.venue.name}</span>}
                {show.venue.line1 && <span className="block">{show.venue.line1}</span>}
                {show.venue.line2 && <span className="block">{show.venue.line2}</span>}
                <span className="block">{[show.venue.city, show.venue.state, show.venue.postal].filter(Boolean).join(" ")}</span>
              </p>
              {show.venue.directions && <p className="hint mt-3 leading-relaxed">{show.venue.directions}</p>}

              <p className="eyebrow mt-5 mb-2">When</p>
              <ul className="space-y-1">
                {show.days.map((day) => (
                  <li key={day.date} className="text-[15px] text-ink-soft">
                    {dayName(day.date)}, {clockTime(day.opens)} to {clockTime(day.closes)}
                  </li>
                ))}
              </ul>
            </Panel>

            <Gallery photos={show.gallery} title={show.title} />
          </div>

          <div className="lg:sticky lg:top-24">
            {show.upcoming && !show.cancelled
              ? <Coming show={show} slug={slug} token={params.get("me")} onDone={reload} />
              : (
                <Panel className="p-6">
                  <h2 className="display display-sm text-ink">This one is over.</h2>
                  <p className="hint mt-2 leading-relaxed">
                    Have a look at what is coming up, or use the mail-in kit, which works from anywhere and is free both ways.
                  </p>
                  <div className="flex flex-wrap gap-2.5 mt-4">
                    <Link to="/shows" className="btn btn-quiet btn-sm">Other shows</Link>
                    <Link to="/get-a-kit" className="btn btn-primary btn-sm">Get a free kit</Link>
                  </div>
                </Panel>
              )}
          </div>
        </div>
      </div>

      <MobileCta label="Tell us you are coming" to={`/shows/${slug}`} />
    </>
  );
}

////////// 3. TELLING US YOU ARE COMING //////////

function Coming({ show, slug, token, onDone }) {
  const [done, setDone] = useState(null);
  const days = show.slots.filter((day) => day.times.length > 0);
  const form = useForm({
    name: "", email: "", phone: "", people: 1, bringing: "",
    day: days[0]?.date || "", time: "", smsConsent: false, website: "",
  });

  const chosenDay = days.find((day) => day.date === form.values.day) || days[0];

  const submit = form.submit(async (values) => {
    const result = await publicApi.registerForShow(slug, { ...values, people: Number(values.people) || 1 });
    analytics.trackRegisterShow(slug, Number(values.people) || 1);
    setDone(result);
    onDone?.();
    return result;
  });

  if (done) {
    return (
      <Panel className="p-6">
        <CheckCircle size={24} className="text-bronze" />
        <h2 className="display display-md text-ink mt-3">You are on the list.</h2>
        <p className="lede mt-2.5 text-muted">
          {dayName(done.day)} at {clockTime(done.time)}. We have emailed you the address and a link to change it.
        </p>
        <p className="hint mt-4 leading-relaxed">
          Your time is when we will be ready for you rather than a hard appointment, so a few minutes either way is
          fine. Nothing to print, nothing to pay, and no obligation to sell anything.
        </p>
      </Panel>
    );
  }

  if (token) {
    return <MySpot slug={slug} token={token} />;
  }

  return (
    <Panel className="p-5 sm:p-6">
      <p className="eyebrow">Optional</p>
      <h2 className="display display-md text-ink mt-1.5">Tell us you are coming.</h2>
      <p className="hint mt-2.5 leading-relaxed">
        You can simply turn up. Registering means we have the right scale out and somebody free when you arrive, which
        on a busy afternoon is the difference between walking in and waiting.
      </p>

      <form onSubmit={submit} className="mt-5 space-y-4" noValidate>
        <Input label="Your name" required {...form.field("name")} />
        <Input label="Email" type="email" required hint="Where the address and your time go" {...form.field("email")} />

        <div className="grid grid-cols-2 gap-4">
          <Input label="Phone" type="tel" hint="Optional" {...form.field("phone")} />
          <Input label="How many of you" type="number" min="1" max="20" {...form.field("people")} />
        </div>

        {days.length > 1 && (
          <Select
            label="Which day"
            options={days.map((day) => ({ value: day.date, label: dayName(day.date) }))}
            {...form.field("day")}
          />
        )}

        <div>
          <p className="label mb-2">What time suits you?</p>
          <div className="flex flex-wrap gap-1.5">
            {chosenDay?.times.map((slot) => (
              <button
                key={slot.time}
                type="button"
                disabled={slot.full}
                onClick={() => form.set("time", slot.time)}
                className={`btn btn-sm ${form.values.time === slot.time ? "btn-ink" : "btn-quiet"} ${slot.full ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                {clockTime(slot.time)}
              </button>
            ))}
          </div>
          {form.errors.time && <p className="error-text mt-1.5">{form.errors.time}</p>}
          <p className="hint mt-2">A guide, not an appointment. Come earlier or later and we will still see you.</p>
        </div>

        <Textarea
          label="What are you thinking of bringing?"
          rows={3}
          hint="Optional, and rough is fine. It tells us which testers to have ready."
          {...form.field("bringing")}
        />

        <Checkbox
          label="Text me if anything about the show changes"
          hint="Only about this show. Never marketing."
          checked={form.values.smsConsent}
          onChange={(event) => form.set("smsConsent", event.target.checked)}
        />

        <input type="text" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" {...form.field("website")} />

        {form.message && <Alert tone={form.message.kind === "success" ? "success" : "error"}>{form.message.text}</Alert>}

        <Button type="submit" loading={form.busy} className="w-full" disabled={!form.values.time}>
          Put me on the list
        </Button>
      </form>
    </Panel>
  );
}

// Somebody who arrived from the link in their confirmation email.
function MySpot({ slug, token }) {
  const { data, loading, error, reload } = useAsync((signal) => publicApi.myShowSpot(slug, token, signal), [slug, token]);
  const [busy, setBusy] = useState(false);

  if (loading) return <Panel className="p-6"><Loading /></Panel>;
  if (error) return <Alert tone="error" title="That link has expired">Register again below and it will replace your old time.</Alert>;

  const cancelled = data.status === "cancelled";

  return (
    <Panel className="p-6">
      <p className="eyebrow">{cancelled ? "Cancelled" : "You are on the list"}</p>
      <h2 className="display display-md text-ink mt-1.5">{data.name}</h2>
      <p className="lede mt-2 text-muted">{dayName(data.day)} at {clockTime(data.time)}</p>
      {data.people > 1 && <p className="hint mt-1">{data.people} people</p>}

      {!cancelled && (
        <Button
          variant="quiet"
          className="mt-5"
          loading={busy}
          onClick={async () => {
            setBusy(true);
            try { await publicApi.cancelShowSpot(slug, token); reload(); } finally { setBusy(false); }
          }}
        >
          Cancel my spot
        </Button>
      )}
      {cancelled && <p className="hint mt-3">Register again any time. Turning up without one is also completely fine.</p>}
    </Panel>
  );
}

////////// 4. PHOTOGRAPHS //////////

function Gallery({ photos, title }) {
  if (!photos?.length) return null;

  return (
    <section className="mt-10">
      <p className="eyebrow mb-4">Photographs</p>
      <StaggerBoxed as="div" step={70} className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {photos.filter((photo) => photo.url).map((photo, index) => (
          <figure key={index} className="m-0">
            <img
              src={photo.url}
              alt={photo.caption || `${title}, photograph ${index + 1}`}
              loading="lazy"
              className="w-full aspect-square object-cover border border-rule"
            />
            {photo.caption && <figcaption className="hint mt-1.5">{photo.caption}</figcaption>}
          </figure>
        ))}
      </StaggerBoxed>
    </section>
  );
}
